import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

// CONSTANTS
import { ContentType, DisplayType } from '@/constants/watchList';
import { LOCATIONS_MODAL } from '@/constants/locations';

// UTILITY
import { analyticsAddedToWatchlist } from '@/utility/analytics';

// STORE
import {
  WatchListActions,
  MovieDetailsActions,
  ShowDetailsActions,
  ContentActions,
} from '@/store/actions';

// COMPONENTS
import MovieHero from '@/containers/MovieDetails/help/Hero';
import ShowHero from '@/containers/ShowDetails/help/Hero';
import useActions from '@/hooks/useActions';

function mapDataForHero(data, key) {
  return {
    [key]: {
      ...data,
      content_title: data.title,
      genres: (Array.isArray(data.genres) ? data.genres : []).map(
        ({ id, name, genre_slug, slug }) => ({
          genre_id: id,
          genre_name: name,
          genre_slug: genre_slug || slug,
        }),
      ),
    },
  };
}

function ShowMovieDetails({
  data,
  swimlaneAnalytics,
  analyticsLocation,
  onChangeStatus,
  isSharedList,
}) {
  const {
    isAuth,
    user,
    actionLoading,
    movieStreamingData,
    movieStreamingDataLoading,
    showStreamingData,
    showStreamingDataLoading,
    partnerData,
  } = useSelector(({ auth, watchList, content, partner, movieDetails, showDetails }) => ({
    isAuth: auth.isAuth,
    user: auth.user,
    actionLoading: watchList.loadings.action || content.loadings.actions[data.id],
    movieStreamingData: movieDetails.streamingDataTemp,
    movieStreamingDataLoading: movieDetails.loadings.streamingDataTemp,
    showStreamingData: showDetails.streamingDataTemp,
    showStreamingDataLoading: showDetails.loadings.streamingDataTemp,
    partnerData: partner.data,
  }));

  const [
    getMovieStreamingData,
    getShowStreamingData,
    changeContentStatus,
    openAddToWLDialogState,
  ] = useActions([
    (movie_id, isTemp) => MovieDetailsActions.getMovieStreamingData.action({ movie_id, isTemp }),
    (show_id, isTemp) => ShowDetailsActions.getShowStreamingData.action({ show_id, isTemp }),
    (content_id, status, callback) => {
      return ContentActions.changeContentStatus.action({
        content_id,
        status,
        callback,
      });
    },
    dialogProps => {
      return WatchListActions.manageAddToWLDialogState.action({
        state: true,
        props: dialogProps,
      });
    },
  ]);

  const { type, id } = data;

  const isMovie =
    typeof type === 'number'
      ? type === ContentType.MOVIE_ID
      : String(type).toLowerCase() === ContentType.MOVIE;

  const getStreamingData = () => {
    if (isMovie) {
      getMovieStreamingData(id, true);
    } else {
      getShowStreamingData(id, true);
    }
  };

  useEffect(getStreamingData, [id, isMovie]);

  function handleChangeWatchList(contentData) {
    const addCallback = () => {
      const properties = {
        content_id: contentData.id,
        content_name: contentData.title || '',
        user_id: user?.id || '',
        partner_id: partnerData?.id || '',
        location: LOCATIONS_MODAL,
        ...swimlaneAnalytics,
      };

      analyticsAddedToWatchlist(properties);
    };

    openAddToWLDialogState({
      data: contentData,
      isSharedList,
      addCallback,
    });
  }

  function handleChangeContentStatus(content_id, status) {
    changeContentStatus(content_id, status, () => {
      onChangeStatus(status);
    });
  }

  return (
    <div>
      {isMovie ? (
        <MovieHero
          analyticsLocation={analyticsLocation}
          data={mapDataForHero(data, 'movie')}
          streamingData={movieStreamingData}
          streamingDataLoading={movieStreamingDataLoading}
          partnerData={partnerData}
          loadingAction={actionLoading}
          addToWatchList={handleChangeWatchList}
          removeFromWatchList={handleChangeWatchList}
          changeContentStatus={handleChangeContentStatus}
          isAuth={isAuth}
          displayType={DisplayType.DIALOG}
        />
      ) : (
        <ShowHero
          analyticsLocation={analyticsLocation}
          streamingData={showStreamingData}
          streamingDataLoading={showStreamingDataLoading}
          data={mapDataForHero(data, 'show')}
          partnerData={partnerData}
          loadingAction={actionLoading}
          addToWatchList={handleChangeWatchList}
          removeFromWatchList={handleChangeWatchList}
          changeContentStatus={handleChangeContentStatus}
          isAuth={isAuth}
          displayType={DisplayType.DIALOG}
        />
      )}
    </div>
  );
}

ShowMovieDetails.defaultProps = {
  swimlaneAnalytics: {},
  onChangeStatus: () => {},
  isSharedList: false,
};

ShowMovieDetails.propTypes = {
  data: PropTypes.object,
  swimlaneAnalytics: PropTypes.object,
  onChangeStatus: PropTypes.func,
  analyticsLocation: PropTypes.string,
  isSharedList: PropTypes.bool,
};

export default React.memo(ShowMovieDetails);
